import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import {
  GlobalSuppliersQuery,
  refetchSupplierIntegrationRequestsQuery,
  useAddGlobalSupplierContactEmailMutation,
  useGlobalSuppliersQuery,
  useInitiateSupplierIntegrationsMutation,
} from '../../generated/graphql';
import { AddSupplierModal } from './AddSupplierModal';
import { EditSupplierModal } from './EditSupplierModal';

type Supplier = GlobalSuppliersQuery['globalSuppliers'][number];

interface SelectionState {
  globalSupplierId: string;
  contactEmail: string;
}

interface Props {
  open: boolean;
  venueId: string;
  existingRequestSupplierIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

const STEPS = ['Select Suppliers', 'Confirm Contact Emails'];

const newContactEmailSchema = yup
  .string()
  .trim()
  .required('Email is required')
  .email('Please enter a valid email address');

export const RequestIntegrationsModal = ({
  open,
  venueId,
  existingRequestSupplierIds,
  onClose,
  onSuccess,
}: Props) => {
  const [activeStep, setActiveStep] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<Set<string>>(
    new Set(),
  );
  const [selections, setSelections] = useState<SelectionState[]>([]);
  const [newEmailInputs, setNewEmailInputs] = useState<Record<string, string>>(
    {},
  );
  const [showNewEmailInput, setShowNewEmailInput] = useState<
    Record<string, boolean>
  >({});
  const [isAddSupplierOpen, setIsAddSupplierOpen] = useState(false);
  const [supplierToEdit, setSupplierToEdit] = useState<Supplier | null>(null);
  const [newEmailFieldErrors, setNewEmailFieldErrors] = useState<
    Record<string, string>
  >({});

  const {
    data: suppliersData,
    loading: suppliersLoading,
    refetch: refetchSuppliers,
  } = useGlobalSuppliersQuery();

  const [initiateIntegrations, { loading: initiating }] =
    useInitiateSupplierIntegrationsMutation({
      refetchQueries: [refetchSupplierIntegrationRequestsQuery({ venueId })],
    });

  const [addContactEmail, { loading: addingEmail }] =
    useAddGlobalSupplierContactEmailMutation();

  const suppliers = useMemo(() => {
    const all = suppliersData?.globalSuppliers ?? [];
    if (!search.trim()) return all;
    const lower = search.toLowerCase();
    return all.filter((s) => s.name.toLowerCase().includes(lower));
  }, [suppliersData, search]);

  const suppliersMap = useMemo(() => {
    const map = new Map<string, Supplier>();
    (suppliersData?.globalSuppliers ?? []).forEach((s) => map.set(s.id, s));
    return map;
  }, [suppliersData]);

  const isAlreadyRequested = (supplierId: string) =>
    existingRequestSupplierIds.includes(supplierId);

  const toggleSupplier = (supplierId: string) => {
    setSelectedSupplierIds((prev) => {
      const next = new Set(prev);
      if (next.has(supplierId)) {
        next.delete(supplierId);
      } else {
        next.add(supplierId);
      }
      return next;
    });
  };

  const handleNext = () => {
    if (selectedSupplierIds.size === 0) {
      toast('Please select at least one supplier', { type: 'warning' });
      return;
    }

    const newSelections: SelectionState[] = Array.from(selectedSupplierIds)
      .map((supplierId) => {
        const supplier = suppliersMap.get(supplierId);
        if (supplier === undefined) {
          return undefined;
        }
        const defaultEmail = supplier.contactEmails[0]?.email ?? '';
        return { globalSupplierId: supplierId, contactEmail: defaultEmail };
      })
      .filter((s): s is SelectionState => s !== undefined);

    if (newSelections.length === 0) {
      toast(
        'Selected suppliers are no longer available. Please refresh and try again.',
        {
          type: 'warning',
        },
      );
      return;
    }

    setSelections(newSelections);
    setActiveStep(1);
  };

  const handleBack = () => setActiveStep(0);

  const handleEmailChange = (globalSupplierId: string, email: string) => {
    setSelections((prev) =>
      prev.map((s) =>
        s.globalSupplierId === globalSupplierId
          ? { ...s, contactEmail: email }
          : s,
      ),
    );
  };

  const handleAddNewEmail = async (supplierId: string) => {
    const raw = newEmailInputs[supplierId] ?? '';
    let email: string;
    try {
      email = newContactEmailSchema.validateSync(raw);
    } catch (e: unknown) {
      const msg =
        e instanceof yup.ValidationError ? e.message : 'Invalid email address';
      setNewEmailFieldErrors((prev) => ({ ...prev, [supplierId]: msg }));
      toast(msg, { type: 'warning' });
      return;
    }

    setNewEmailFieldErrors((prev) => {
      const next = { ...prev };
      delete next[supplierId];
      return next;
    });

    const result = await addContactEmail({
      variables: { supplierId, email },
      toastConfig: null,
    });

    if (!result?.data?.addGlobalSupplierContactEmail) {
      return;
    }

    handleEmailChange(supplierId, email);
    setNewEmailInputs((prev) => ({ ...prev, [supplierId]: '' }));
    setShowNewEmailInput((prev) => ({ ...prev, [supplierId]: false }));
    await refetchSuppliers();
    toast('Contact email added', { type: 'success' });
  };

  const handleConfirm = async () => {
    const invalidSelections = selections.filter((s) => !s.contactEmail);
    if (invalidSelections.length > 0) {
      toast('Please select a contact email for all suppliers', {
        type: 'warning',
      });
      return;
    }

    const result = await initiateIntegrations({
      variables: {
        venueId,
        selections,
      },
      toastConfig: null,
    });

    const created = result?.data?.initiateSupplierIntegrations;
    if (!created?.length) {
      return;
    }

    toast(`Successfully initiated ${created.length} integration request(s)`, {
      type: 'success',
    });
    onSuccess();
    handleClose();
  };

  const handleClose = () => {
    setActiveStep(0);
    setSearch('');
    setSelectedSupplierIds(new Set());
    setSelections([]);
    setNewEmailInputs({});
    setShowNewEmailInput({});
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Request New Integrations
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box>
              <Box display="flex" gap={2} mb={2} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search suppliers…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setIsAddSupplierOpen(true)}
                >
                  Add New Supplier
                </Button>
              </Box>

              {suppliersLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box
                  sx={{
                    maxHeight: 420,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  {suppliers.length === 0 ? (
                    <Box py={4} textAlign="center">
                      <Typography color="text.secondary" variant="body2">
                        No suppliers found
                      </Typography>
                    </Box>
                  ) : (
                    suppliers.map((supplier, idx) => {
                      const alreadyRequested = isAlreadyRequested(supplier.id);
                      const isSelected = selectedSupplierIds.has(supplier.id);
                      let supplierTooltipTitle = '';
                      if (alreadyRequested) {
                        supplierTooltipTitle =
                          'Integration already requested for this venue';
                      } else if (supplier.exceptionFlag) {
                        supplierTooltipTitle =
                          supplier.exceptionReason ||
                          'This supplier cannot be contacted via automated email';
                      }

                      return (
                        <Box key={supplier.id}>
                          {idx > 0 && <Divider />}
                          <Box
                            display="flex"
                            alignItems="center"
                            px={2}
                            py={1}
                            sx={{
                              opacity:
                                alreadyRequested || supplier.exceptionFlag
                                  ? 0.5
                                  : 1,
                              bgcolor: isSelected
                                ? 'action.selected'
                                : undefined,
                            }}
                          >
                            <Tooltip
                              title={supplierTooltipTitle}
                              disableHoverListener={
                                !alreadyRequested && !supplier.exceptionFlag
                              }
                            >
                              <span>
                                <Checkbox
                                  checked={isSelected}
                                  disabled={
                                    alreadyRequested || !!supplier.exceptionFlag
                                  }
                                  onChange={() => toggleSupplier(supplier.id)}
                                  size="small"
                                />
                              </span>
                            </Tooltip>

                            <Box flex={1} ml={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" fontWeight={500}>
                                  {supplier.name}
                                </Typography>
                                {supplier.exceptionFlag && (
                                  <Tooltip
                                    title={
                                      supplier.exceptionReason ||
                                      'Cannot be contacted via automated email'
                                    }
                                  >
                                    <InfoOutlinedIcon
                                      fontSize="small"
                                      color="warning"
                                    />
                                  </Tooltip>
                                )}
                                {alreadyRequested && (
                                  <Chip
                                    label="Already requested"
                                    size="small"
                                    color="default"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                              {supplier.emailDomain && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {supplier.emailDomain}
                                </Typography>
                              )}
                            </Box>

                            <Tooltip title="Edit supplier">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSupplierToEdit(supplier);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              )}

              {selectedSupplierIds.size > 0 && (
                <Typography variant="body2" color="primary" mt={1}>
                  {selectedSupplierIds.size} supplier(s) selected
                </Typography>
              )}
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Confirm the contact email for each supplier. The first request
                email will be sent to these addresses.
              </Alert>
              <Stack spacing={2}>
                {selections.map((selection) => {
                  const supplier = suppliersMap.get(selection.globalSupplierId);
                  if (!supplier) return null;
                  const showAddInput =
                    showNewEmailInput[selection.globalSupplierId];

                  return (
                    <Box
                      key={selection.globalSupplierId}
                      p={2}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={2}
                      >
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={500}>
                            {supplier.name}
                          </Typography>
                        </Box>

                        <Box sx={{ minWidth: 300 }}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Contact Email</InputLabel>
                            <Select
                              value={selection.contactEmail}
                              label="Contact Email"
                              onChange={(e) =>
                                handleEmailChange(
                                  selection.globalSupplierId,
                                  e.target.value,
                                )
                              }
                            >
                              {supplier.contactEmails.map((ce) => (
                                <MenuItem key={ce.id} value={ce.email}>
                                  {ce.email}
                                </MenuItem>
                              ))}
                              <Divider />
                              <MenuItem
                                value=""
                                onClick={(e) => {
                                  e.preventDefault();
                                  setShowNewEmailInput((prev) => ({
                                    ...prev,
                                    [selection.globalSupplierId]: true,
                                  }));
                                }}
                                sx={{ color: 'primary.main' }}
                              >
                                <AddIcon
                                  fontSize="small"
                                  sx={{ mr: 1, verticalAlign: 'middle' }}
                                />
                                Add new contact email
                              </MenuItem>
                            </Select>
                          </FormControl>

                          {showAddInput && (
                            <Box display="flex" gap={1} mt={1}>
                              <TextField
                                size="small"
                                placeholder="new@email.com"
                                type="email"
                                autoComplete="email"
                                value={
                                  newEmailInputs[selection.globalSupplierId] ??
                                  ''
                                }
                                error={
                                  !!newEmailFieldErrors[
                                    selection.globalSupplierId
                                  ]
                                }
                                helperText={
                                  newEmailFieldErrors[
                                    selection.globalSupplierId
                                  ]
                                }
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setNewEmailInputs((prev) => ({
                                    ...prev,
                                    [selection.globalSupplierId]: v,
                                  }));
                                  if (
                                    newEmailFieldErrors[
                                      selection.globalSupplierId
                                    ]
                                  ) {
                                    setNewEmailFieldErrors((prev) => {
                                      const next = { ...prev };
                                      delete next[selection.globalSupplierId];
                                      return next;
                                    });
                                  }
                                }}
                                fullWidth
                              />
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() =>
                                  handleAddNewEmail(selection.globalSupplierId)
                                }
                                disabled={addingEmail}
                              >
                                Add
                              </Button>
                              <Button
                                size="small"
                                onClick={() =>
                                  setShowNewEmailInput((prev) => ({
                                    ...prev,
                                    [selection.globalSupplierId]: false,
                                  }))
                                }
                              >
                                Cancel
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {activeStep === 0 ? (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={selectedSupplierIds.size === 0}
              >
                Next ({selectedSupplierIds.size} selected)
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleBack} disabled={initiating}>
                Back
              </Button>
              <Button onClick={handleClose} disabled={initiating}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={initiating}
                startIcon={
                  initiating ? <CircularProgress size={16} /> : undefined
                }
              >
                Send {selections.length} Email(s)
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <AddSupplierModal
        open={isAddSupplierOpen}
        onClose={() => setIsAddSupplierOpen(false)}
        onCreated={() => {
          setIsAddSupplierOpen(false);
          refetchSuppliers();
        }}
      />

      <EditSupplierModal
        open={!!supplierToEdit}
        supplier={supplierToEdit}
        onClose={() => setSupplierToEdit(null)}
        onUpdated={() => {
          setSupplierToEdit(null);
          refetchSuppliers();
        }}
      />
    </>
  );
};
